import * as Sentry from '@sentry/nextjs';

/**
 * Error context for server actions
 */
interface ServerActionErrorContext {
  action: string;
  step?: string;
  userId?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Recoverable transient notices we never want in Sentry.
 *
 * These are messages emitted by PostgREST / the Supabase JS client that
 * represent self-healing conditions (the client retries automatically).
 * Capturing them as errors creates noise and pages on-call for non-incidents.
 *
 * Add patterns sparingly — only known-recoverable, well-understood messages.
 * NEVER broaden a pattern to suppress real errors.
 */
const RECOVERABLE_ERROR_PATTERNS: readonly RegExp[] = [
  // PostgREST: schema cache reload in progress. Client auto-retries.
  // Surfaces in cron paths (BALLEE-CG / BALLEE-CH / BALLEE-CJ).
  /schema cache.*retrying/i,
];

/**
 * Returns true when the message matches a known-recoverable transient
 * pattern and should be skipped from Sentry capture.
 */
function isRecoverableErrorMessage(message: string | undefined): boolean {
  if (!message) return false;
  return RECOVERABLE_ERROR_PATTERNS.some((re) => re.test(message));
}

/**
 * Extracts a best-effort error message from an unknown value.
 */
function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return '';
}

/**
 * Captures server action errors with full context for Sentry
 *
 * This function reports errors to Sentry BEFORE they reach Next.js's error boundary,
 * ensuring full error details are captured even in production where Next.js obscures
 * error messages for security.
 *
 * @param error - The error to capture
 * @param context - Contextual information about the error
 *
 * @example
 * ```typescript
 * try {
 *   const result = await dangerousOperation();
 *   if (!result.success) {
 *     captureServerActionError(new Error(result.error), {
 *       action: 'createUser',
 *       step: 'database_insert',
 *       userId: user.id,
 *       data: { email: user.email },
 *     });
 *     throw new Error(result.error);
 *   }
 * } catch (error) {
 *   captureServerActionError(error, { action: 'createUser' });
 *   throw error;
 * }
 * ```
 */
export function captureServerActionError(
  error: Error | unknown,
  context: ServerActionErrorContext,
): void {
  // Determine error type for better categorization
  let errorType = 'unknown';
  let errorMessage = 'Unknown error';
  const extraData: Record<string, unknown> = {};

  if (error instanceof Error) {
    errorType = error.constructor.name;
    errorMessage = error.message;
    extraData.stack = error.stack;
  } else if (typeof error === 'string') {
    errorType = 'string_error';
    errorMessage = error;
  } else if (error && typeof error === 'object') {
    errorType = 'object_error';
    errorMessage = JSON.stringify(error);
    extraData.errorObject = error;
  }

  // Skip Sentry capture for recoverable transient notices (e.g. PostgREST
  // schema-cache reload). The client auto-retries — capturing as an error
  // pages on-call for a non-incident. Also check the original error's
  // message in case the ServiceError wrapping discards the inner detail.
  if (
    isRecoverableErrorMessage(errorMessage) ||
    isRecoverableErrorMessage(extractErrorMessage(error))
  ) {
    return;
  }

  // Capture to Sentry with full context
  Sentry.captureException(error, {
    tags: {
      action: context.action,
      errorType,
      step: context.step || 'unknown',
    },
    extra: {
      errorMessage,
      ...extraData,
      context: {
        ...context,
        // Remove action from context since it's in tags
        action: undefined,
      },
    },
    user: context.userId
      ? {
          id: context.userId,
        }
      : undefined,
  });
}

/**
 * Captures validation errors (Zod) with structured field-level details
 *
 * @param error - The Zod validation error
 * @param context - Contextual information
 */
export function captureValidationError(
  error: unknown,
  context: ServerActionErrorContext,
): void {
  // Check if it's a Zod error by duck-typing
  const isZodError =
    error &&
    typeof error === 'object' &&
    'issues' in error &&
    Array.isArray((error as { issues: unknown }).issues);

  if (isZodError) {
    const zodError = error as {
      issues: Array<{ path: string[]; message: string }>;
    };

    Sentry.captureException(error, {
      tags: {
        action: context.action,
        errorType: 'validation_error',
        step: context.step || 'validation',
      },
      extra: {
        validationErrors: zodError.issues.map((issue) => ({
          field: issue.path.join('.'),
          message: issue.message,
        })),
        context: {
          ...context,
          action: undefined,
        },
      },
      user: context.userId
        ? {
            id: context.userId,
          }
        : undefined,
    });
  } else {
    // Fallback to regular error capture
    captureServerActionError(error, {
      ...context,
      errorType: 'validation_error',
    });
  }
}

/**
 * Captures authorization errors with user context
 *
 * @param error - The authorization error
 * @param context - Contextual information including userId
 */
export function captureAuthorizationError(
  error: Error | unknown,
  context: ServerActionErrorContext & {
    userId?: string;
    requiredRole?: string;
  },
): void {
  const errorMessage =
    error instanceof Error ? error.message : 'Authorization failed';

  Sentry.captureException(error, {
    tags: {
      action: context.action,
      errorType: 'authorization_error',
      requiredRole: context.requiredRole || 'unknown',
    },
    extra: {
      errorMessage,
      userId: context.userId,
      context: {
        ...context,
        action: undefined,
        userId: undefined,
        requiredRole: undefined,
      },
    },
    user: context.userId
      ? {
          id: context.userId,
        }
      : undefined,
  });
}

/**
 * Marks a thrown Error so that Sentry's global `beforeSend` skips it.
 *
 * Use after a service has already called `captureServiceError` (or any other
 * inline capture with rich context) and is about to re-throw. Without this
 * mark, the outer Next.js `onRequestError` handler captures a SECOND,
 * less-tagged event for the same failure — see BALLEE-CD / BALLEE-CE.
 *
 * The matching filter lives in `packages/monitoring/sentry/src/sentry.server.config.ts`
 * (and `sentry.client.config.ts`).
 */
export function markErrorAsAlreadyCaptured<E extends Error>(error: E): E {
  (error as E & { alreadyCapturedToSentry?: boolean }).alreadyCapturedToSentry =
    true;
  return error;
}

/**
 * Captures service layer errors with Result pattern details
 *
 * @param error - The service error
 * @param context - Contextual information
 */
export function captureServiceError(
  error: { message: string; code?: string; details?: unknown },
  context: ServerActionErrorContext,
): void {
  Sentry.captureException(new Error(error.message), {
    tags: {
      action: context.action,
      errorType: 'service_error',
      errorCode: error.code || 'unknown',
    },
    extra: {
      errorMessage: error.message,
      errorCode: error.code,
      errorDetails: error.details,
      context: {
        ...context,
        action: undefined,
      },
    },
    user: context.userId
      ? {
          id: context.userId,
        }
      : undefined,
  });
}

/**
 * Captures a service-layer warning (non-error, informational) with context.
 *
 * Use this for transient / infrastructure failures that are not actionable
 * code bugs (e.g. Cloudflare 522, Supabase momentary outage) where the
 * caller handles degraded output gracefully. Emits a Sentry `warning`-level
 * message rather than an exception so it doesn't inflate error counts.
 */
export function captureServiceWarning(
  warning: { message: string; code?: string; details?: unknown },
  context: ServerActionErrorContext,
): void {
  Sentry.captureMessage(warning.message, {
    level: 'warning',
    tags: {
      action: context.action,
      errorType: 'service_warning',
      errorCode: warning.code || 'unknown',
    },
    extra: {
      warningDetails: warning.details,
      context: {
        ...context,
        action: undefined,
      },
    },
    user: context.userId ? { id: context.userId } : undefined,
  });
}

/**
 * Wraps a server action with automatic error capture
 *
 * This higher-order function wraps server actions to automatically capture
 * errors to Sentry before they reach Next.js's error boundary.
 *
 * @param actionName - Name of the action for logging
 * @param handler - The async function to wrap
 * @returns Wrapped function with error handling
 *
 * @example
 * ```typescript
 * export const createUserAction = withErrorCapture(
 *   'createUserAction',
 *   async (data: UserData) => {
 *     const result = await createUser(data);
 *     return result;
 *   }
 * );
 * ```
 */
export function withErrorCapture<TArgs extends unknown[], TReturn>(
  actionName: string,
  handler: (...args: TArgs) => Promise<TReturn>,
): (...args: TArgs) => Promise<TReturn> {
  return async (...args: TArgs): Promise<TReturn> => {
    try {
      return await handler(...args);
    } catch (error) {
      captureServerActionError(error, {
        action: actionName,
        step: 'execution',
      });
      throw error;
    }
  };
}

/**
 * Execute a block with Sentry error capture
 *
 * This utility wraps a try-catch block to automatically capture errors to Sentry.
 * Useful for inline error handling in server actions.
 *
 * @param context - Error context information
 * @param handler - The async function to execute
 * @returns The result of the handler
 *
 * @example
 * ```typescript
 * export async function updateUserAction(data: UserData) {
 *   return executeWithErrorCapture(
 *     { action: 'updateUserAction', userId: user.id },
 *     async () => {
 *       const guardResult = await requireSuperAdmin();
 *       if (!guardResult.isSuccess) {
 *         const error = guardResult.getError();
 *         captureAuthorizationError(error, {
 *           action: 'updateUserAction',
 *           requiredRole: 'super_admin',
 *         });
 *         throw new Error(error.message);
 *       }
 *
 *       const { userClient } = guardResult.getValue();
 *       const result = await updateUser(userClient, data);
 *       return result;
 *     }
 *   );
 * }
 * ```
 */
export async function executeWithErrorCapture<TReturn>(
  context: ServerActionErrorContext,
  handler: () => Promise<TReturn>,
): Promise<TReturn> {
  try {
    return await handler();
  } catch (error) {
    // Only capture if not already captured (check if it's a known error type)
    const isZodError =
      error &&
      typeof error === 'object' &&
      'issues' in error &&
      Array.isArray((error as { issues: unknown }).issues);

    if (!isZodError) {
      captureServerActionError(error, {
        ...context,
        step: context.step || 'execution',
      });
    }
    throw error;
  }
}
